// apps/client/src/game/interactions/door.ts (M1-T16) — house doors (§6.4 "Spaces", Contract change 6). Outdoors: the front door of a
// plot a resident lives on → room 0 of that house (the server answers the door setting: a locked door or an owner who is out shows its
// toast). Indoors: the nearest exit of the room → the next room, or outdoors ("Leave"). Ranges match the server's: tuning.town.doorRange
// from the door tile's centre. Like the mailbox, a door counts only when you face it (or stand on it), so the two never fight.
import { tuning, type StringKey } from '@whiskerton/content';
import { houseSpace, type SpaceId, type TileXY, type Vec2 } from '@whiskerton/shared';
import type { GameStore } from '../../state/types';
import { isRoomScene } from '../../world/spaces/room';
import type { Candidate, InteractCtx, Interaction } from '../interact';

const PREFIX = 'door:';
const centre = (t: TileXY): Vec2 => ({ x: t.x + 0.5, y: t.y + 0.5 });
/** The candidate id carries the space it leads to. */
const candidate = (to: SpaceId, at: Vec2): Candidate =>
  ({ id: `${PREFIX}${to}`, at, verb: (to === 'out' ? 'decor.verbLeave' : 'hud.verbEnter') as StringKey, priority: 70 });
const residentOn = (store: GameStore, plot: number) => Object.values(store.residents).find((r) => r.plot === plot) ?? null;
/** Within range and in front of you (or right where you stand). */
function reach(ctx: InteractCtx, at: Vec2): number | null {
  const dx = at.x - ctx.pos.x, dy = at.y - ctx.pos.y, d = Math.hypot(dx, dy);
  if (d > tuning.town.doorRange) return null;
  if (d > 0.6 && (dx * Math.sin(ctx.f) + dy * Math.cos(ctx.f)) / d < 0.2) return null;
  return d;
}
/** Every door you could use from here: plot doors outdoors, the room's exits indoors. */
function doors(ctx: InteractCtx): { to: SpaceId; at: TileXY }[] {
  if (ctx.space === 'out') {
    return (ctx.store.map?.plots ?? []).flatMap((p) => { const r = residentOn(ctx.store, p.index); return r ? [{ to: houseSpace(r.id, 0), at: p.door }] : []; });
  }
  const s = ctx.game.scenes.current;
  return ctx.space.startsWith('house:') && isRoomScene(s) ? s.view.exits : [];
}

export const interaction: Interaction = {
  id: 'door',
  find(ctx) {
    let best: { to: SpaceId; at: Vec2; d: number } | null = null;
    for (const door of doors(ctx)) {
      const at = centre(door.at), d = reach(ctx, at);
      if (d !== null && (!best || d < best.d)) best = { to: door.to, at, d };
    }
    return best && candidate(best.to, best.at);
  },
  run(ctx, c) { void ctx.game.cmd.enterSpace(c.id.slice(PREFIX.length) as SpaceId); },
  /** A tap on a resident's house (or its door tile), or on an exit indoors: walk to the door, then go through. */
  fromTap(ctx, hit) {
    const t = hit.tile;
    if (ctx.space === 'out') {
      const plots = ctx.store.map?.plots ?? [];
      const o = hit.object, plot = o?.kind === 'house' ? (o.state as { plot?: number }).plot : plots.find((p) => p.door.x === t.x && p.door.y === t.y)?.index;
      const p = typeof plot === 'number' ? plots[plot] : undefined, r = p && residentOn(ctx.store, p.index);
      return p && r ? candidate(houseSpace(r.id, 0), centre(p.door)) : null;
    }
    const exit = doors(ctx).find((e) => e.at.x === t.x && e.at.y === t.y);
    return exit ? candidate(exit.to, centre(exit.at)) : null;
  },
};
